import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Tooltip,
} from '@mui/material'

import {
  Avatar,
  Button,
  Chip,
  Dropdown,
  Icon,
  IconButton,
  TextField,
  Typography,
} from '~components/internal'
import type { DropdownOption } from '~components/internal/Dropdown'
import { TextFieldLabel } from '~components/internal/TextField'
import { orgHandlesMatch } from '~hooks/useUrlOrganizationHandle'
import { type RootState } from '~redux/rootReducer'
import { setSelectedEIOrganization } from '~redux/slices/organizationSlice'
import { type AccessAction, type UserId } from '~types'
import { isValidEmail } from '~utils/emailUtils'

import './ManageAdHocDialog.scss'

const ACTION_LABEL_MAP: Record<string, string> = {
  read: 'Can view',
  write: 'Can edit',
}

const formatActionLabel = (action: string): string =>
  ACTION_LABEL_MAP[action] ?? action.charAt(0).toUpperCase() + action.slice(1)

export type AdHocUserEntry = {
  id: string
  userId: UserId
  name: string
  email: string | null
  image: string | null
  adHocActions: Set<AccessAction>
}

export type GroupMember = {
  userId: string
  name: string
  email: string | null
  image: string | null
}

export type GroupPermissionEntry = {
  groupId: string
  name: string
  actions: Set<string>
  members: GroupMember[]
}

export interface ManageAdHocDialogProps {
  open: boolean
  onClose: () => void
  title: string
  description: string
  accessLabel: string
  organizationHandle: string
  allowedActions: AccessAction[]
  disabledActions?: AccessAction[]
  actionDescriptions?: Partial<Record<AccessAction, string>>
  loading: boolean
  saving: boolean
  error: string | null
  adHocUsers: AdHocUserEntry[]
  groupPermissions: GroupPermissionEntry[]
  suggestedMembers?: GroupMember[]
  hasPendingChanges: boolean
  onToggle: (targetUserId: UserId, action: AccessAction, currentlyHasAdHoc: boolean) => void
  onSave: () => void
  onAddUsers: (emails: string[], actions: Set<AccessAction>, sendEmail: boolean) => Promise<void>
  getEffectiveState: (entry: AdHocUserEntry, action: AccessAction) => { checked: boolean }
}

const ManageAdHocDialog: React.FC<ManageAdHocDialogProps> = ({
  open,
  onClose,
  title,
  description,
  accessLabel,
  organizationHandle,
  allowedActions,
  disabledActions = [],
  actionDescriptions,
  loading,
  saving,
  error,
  adHocUsers,
  groupPermissions,
  suggestedMembers: providedSuggestedMembers,
  hasPendingChanges,
  onToggle,
  onSave,
  onAddUsers,
  getEffectiveState,
}) => {
  const dispatch = useDispatch()
  const emailIdentities = useSelector(
    (state: RootState) => state?.organization.emailIdentities ?? [],
  )
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const [emails, setEmails] = useState<string[]>([])
  const [adding, setAdding] = useState(false)
  const [sendEmail, setSendEmail] = useState(true)
  const [usersToRemove, setUsersToRemove] = useState<Map<UserId, Set<AccessAction>>>(new Map())
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [addAction, setAddAction] = useState<AccessAction>(
    allowedActions.includes('read' as AccessAction) ? ('read' as AccessAction) : allowedActions[0],
  )

  useEffect(() => {
    if (open) {
      setUsersToRemove(new Map())
      setShowRemoveConfirm(false)
    }
  }, [open])

  useEffect(() => {
    if (!hasPendingChanges) {
      setUsersToRemove(new Map())
    }
  }, [hasPendingChanges])

  const actionOptions: DropdownOption[] = useMemo(
    () =>
      allowedActions.map((action) => ({
        value: action,
        label: formatActionLabel(action),
        tooltip: actionDescriptions?.[action],
      })),
    [allowedActions, actionDescriptions],
  )

  const tableActionOptions: DropdownOption[] = useMemo(
    () =>
      actionOptions.map((opt) => ({
        ...opt,
        disabled: disabledActions.includes(opt.value as AccessAction),
      })),
    [actionOptions, disabledActions],
  )

  const suggestedMembers = useMemo(() => {
    const seen = new Set<string>()
    const members: GroupMember[] = []
    const sourceMembers =
      providedSuggestedMembers ?? groupPermissions.flatMap((group) => group.members)
    for (const member of sourceMembers) {
      if (member.email && !seen.has(member.email)) {
        seen.add(member.email)
        members.push(member)
      }
    }
    return members
  }, [groupPermissions, providedSuggestedMembers])

  const handleAddUser = useCallback(async () => {
    if (emails.length === 0) return
    setAdding(true)
    try {
      const actions =
        addAction === 'write'
          ? new Set<AccessAction>(['read', 'write'])
          : new Set<AccessAction>([addAction])
      await onAddUsers(emails, actions, sendEmail)
      setEmails([])
      setAddAction(allowedActions[0])
    } catch {
      // Error should be handled by the onAddUsers callback
    } finally {
      setAdding(false)
    }
  }, [emails, addAction, sendEmail, onAddUsers, allowedActions])

  const getAccessDropdown = useCallback(
    (row: AdHocUserEntry) => {
      const checkedActions = allowedActions.filter((a) => getEffectiveState(row, a).checked)
      const currentAction = checkedActions.includes('write' as AccessAction)
        ? 'write'
        : checkedActions.length > 0
          ? checkedActions[0]
          : ''
      return (
        <Dropdown
          options={tableActionOptions}
          value={currentAction}
          onChange={(value) => {
            const selected = Array.isArray(value) ? value[0] : value
            const desiredActions = new Set<string>(
              selected === 'write' ? ['read', 'write'] : [selected],
            )
            for (const action of allowedActions) {
              const wasChecked = getEffectiveState(row, action).checked
              const shouldBeChecked = desiredActions.has(action)
              if (wasChecked !== shouldBeChecked) {
                onToggle(row.userId, action, row.adHocActions.has(action))
              }
            }
          }}
          placeholder='Select action'
          showSelectedTooltip
        />
      )
    },
    [allowedActions, tableActionOptions, getEffectiveState, onToggle],
  )

  const toggleRemoveQueue = useCallback(
    (row: AdHocUserEntry) => {
      const queuedActions = usersToRemove.get(row.userId)
      if (queuedActions) {
        for (const action of queuedActions) {
          onToggle(row.userId, action, row.adHocActions.has(action))
        }
        setUsersToRemove((prev) => {
          const next = new Map(prev)
          next.delete(row.userId)
          return next
        })
      } else {
        const toggled = new Set<AccessAction>()
        for (const action of allowedActions) {
          if (getEffectiveState(row, action).checked) {
            onToggle(row.userId, action, row.adHocActions.has(action))
            toggled.add(action)
          }
        }
        setUsersToRemove((prev) => new Map(prev).set(row.userId, toggled))
      }
    },
    [usersToRemove, allowedActions, getEffectiveState, onToggle],
  )

  const usersQueuedForRemoval = useMemo(
    () => adHocUsers.filter((u) => usersToRemove.has(u.userId)),
    [adHocUsers, usersToRemove],
  )

  const handleSaveClick = useCallback(() => {
    if (usersToRemove.size > 0) {
      setShowRemoveConfirm(true)
    } else {
      onSave()
    }
  }, [usersToRemove, onSave])

  const handleConfirmRemove = useCallback(() => {
    setShowRemoveConfirm(false)
    onSave()
  }, [onSave])

  const closeAndClearEmails = (): void => {
    onClose()
    setEmails([])
  }

  return (
    <Dialog
      open={open}
      onClose={closeAndClearEmails}
      maxWidth='sm'
      fullWidth
      className='manage-ad-hoc-dialog'
    >
      <DialogTitle className='manage-ad-hoc-dialog__title'>
        <div>
          <Typography variant='h4'>{title}</Typography>
          <Typography variant='body2' color='textSecondary'>
            {description}
          </Typography>
        </div>
        <Tooltip title='Close'>
          <IconButton size='small' onClick={closeAndClearEmails}>
            <Icon name='close' fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent className='manage-ad-hoc-dialog__content'>
        {loading ? (
          <div className='manage-ad-hoc-dialog__loading'>
            <CircularProgress size={32} />
          </div>
        ) : (
          <>
            {error && (
              <Typography variant='body2' className='manage-ad-hoc-dialog__error'>
                {error}
              </Typography>
            )}
            <div className='manage-ad-hoc-dialog__add-user'>
              <div className='manage-ad-hoc-dialog__add-user-row'>
                <div className='manage-ad-hoc-dialog__add-user-email'>
                  <Autocomplete<string | GroupMember, true, false, true>
                    multiple
                    freeSolo
                    options={suggestedMembers.filter(
                      (m) =>
                        m.email &&
                        !emails.includes(m.email) &&
                        !adHocUsers.some((u) => u.email === m.email),
                    )}
                    value={emails}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : (option.email ?? '')
                    }
                    filterOptions={(options, state) => {
                      const trimmed = state.inputValue.trim()
                      const input = trimmed.toLowerCase()
                      const matches = options.filter(
                        (opt): opt is GroupMember =>
                          typeof opt !== 'string' &&
                          (opt.email?.toLowerCase().includes(input) ||
                            opt.name.toLowerCase().includes(input)),
                      )
                      const isExistingMatch = matches.some(
                        (opt) => opt.email?.toLowerCase() === input,
                      )
                      if (trimmed.length > 0 && !isExistingMatch && !emails.includes(trimmed)) {
                        return [trimmed, ...matches]
                      }
                      return matches
                    }}
                    onChange={(_event, newValue) =>
                      setEmails(newValue.map((v) => (typeof v === 'string' ? v : (v.email ?? ''))))
                    }
                    renderOption={(props, option) => {
                      if (typeof option === 'string') {
                        return (
                          <li {...props} key={`add-${option}`}>
                            <div className='manage-ad-hoc-dialog__add-option'>
                              <Icon name='email' fontSize='small' color='textSecondary' />
                              <Typography variant='body2' noWrap>
                                {`Add "${option}"`}
                              </Typography>
                            </div>
                          </li>
                        )
                      }
                      return (
                        <li {...props} key={option.userId}>
                          <div className='manage-ad-hoc-dialog__user-cell'>
                            <Avatar size='xs' src={option.image || undefined} alt={option.name} />
                            <div className='manage-ad-hoc-dialog__user-info'>
                              <Typography variant='body2' noWrap>
                                {option.name}
                              </Typography>
                              {option.email && (
                                <Typography variant='caption' color='textSecondary' noWrap>
                                  {option.email}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </li>
                      )
                    }}
                    renderValue={(value: (string | GroupMember)[], getItemProps) =>
                      value.map((option, index: number) => {
                        const label = typeof option === 'string' ? option : (option.email ?? '')
                        return (
                          <Chip
                            {...getItemProps({ index })}
                            label={label}
                            size='small'
                            variant='outlined'
                            color={isValidEmail(label) ? 'neutral' : 'red'}
                          />
                        )
                      })
                    }
                    renderInput={({ slotProps, ...params }) => (
                      <TextField
                        {...params}
                        InputProps={{ ...slotProps.input }}
                        inputProps={slotProps.htmlInput}
                        label='Add user by email'
                        placeholder={emails.length === 0 ? 'Type or paste emails' : ''}
                        error={emails.some((email) => !isValidEmail(email))}
                        errorText={
                          emails.some((email) => !isValidEmail(email))
                            ? ((): string => {
                                const invalid = emails.filter((email) => !isValidEmail(email))
                                const shown = invalid.slice(0, 3).join(', ')
                                return `One or more emails are invalid: ${shown}${invalid.length > 3 ? '...' : ''}`
                              })()
                            : undefined
                        }
                      />
                    )}
                  />
                </div>
                {emails.length > 0 && (
                  <div className='manage-ad-hoc-dialog__add-user-actions'>
                    <TextFieldLabel id='manage-ad-hoc-add-action' label='Access level' />
                    <Dropdown
                      id='manage-ad-hoc-add-action'
                      options={actionOptions.map((opt) => ({
                        ...opt,
                        disabled: disabledActions.includes(opt.value as AccessAction),
                      }))}
                      value={addAction}
                      onChange={(value) => {
                        const selected = Array.isArray(value) ? value[0] : value
                        setAddAction(selected as AccessAction)
                      }}
                      placeholder='Select action'
                      fullWidth
                      showSelectedTooltip
                    />
                  </div>
                )}
              </div>
              {emails.length > 0 && (
                <div className='manage-ad-hoc-dialog__add-user-submit'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendEmail}
                        onChange={() => setSendEmail((prev) => !prev)}
                        size='small'
                      />
                    }
                    label='Send confirmation email to added users'
                  />
                </div>
              )}
            </div>
            {emails.length === 0 && (
              <>
                <div className='manage-ad-hoc-dialog__user-list'>
                  <Typography variant='body1' className='manage-ad-hoc-dialog__section-label'>
                    Direct access
                  </Typography>
                  {adHocUsers.length === 0 ? (
                    <Typography
                      variant='body2'
                      color='textSecondary'
                      className='manage-ad-hoc-dialog__user-list-empty'
                    >
                      {`No users have been granted ${accessLabel} access yet`}
                    </Typography>
                  ) : (
                    <>
                      {adHocUsers.map((row) => {
                        const queued = usersToRemove.has(row.userId)
                        return (
                          <div
                            key={row.id}
                            className={`manage-ad-hoc-dialog__user-list-item${
                              queued ? ' manage-ad-hoc-dialog__user-list-item--removing' : ''
                            }`}
                          >
                            <div className='manage-ad-hoc-dialog__user-cell'>
                              <Avatar size='xs' src={row.image || undefined} alt={row.name} />
                              <div className='manage-ad-hoc-dialog__user-info'>
                                <Typography variant='body2' noWrap>
                                  {row.name}
                                </Typography>
                                {row.email && (
                                  <Typography variant='caption' color='textSecondary' noWrap>
                                    {row.email}
                                  </Typography>
                                )}
                              </div>
                            </div>

                            <div className='manage-ad-hoc-dialog__list-action'>
                              {queued ? (
                                <Typography
                                  variant='body2'
                                  color='textSecondary'
                                  className='manage-ad-hoc-dialog__user-queued'
                                >
                                  Will be removed on save
                                </Typography>
                              ) : (
                                getAccessDropdown(row)
                              )}
                              <Tooltip title={queued ? 'Undo remove' : 'Remove access'}>
                                <IconButton size='small' onClick={() => toggleRemoveQueue(row)}>
                                  <Icon name={queued ? 'undo' : 'remove'} fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        )
                      })}
                    </>
                  )}
                </div>

                {groupPermissions.length > 0 && (
                  <div className='manage-ad-hoc-dialog__groups'>
                    <Typography variant='body1' className='manage-ad-hoc-dialog__section-label'>
                      Groups with access via roles
                    </Typography>
                    <div>
                      <Typography variant='body2' color='textSecondary'>
                        A group's access is determined by its role(s) and cannot be changed here.
                      </Typography>
                    </div>
                    {groupPermissions.map((group) => (
                      <div key={group.groupId} className='manage-ad-hoc-dialog__group-row'>
                        <div className='manage-ad-hoc-dialog__group-left'>
                          <Icon name='group' fontSize='small' color='textSecondary' />
                          <Tooltip title='Manage this group in access controls'>
                            <Link
                              component={RouterLink}
                              to='/settings-hub/groups'
                              variant='body2'
                              underline='hover'
                              onClick={() => {
                                const organization = emailIdentities.find((identity) =>
                                  orgHandlesMatch(identity.handle, organizationHandle),
                                )
                                if (organization && organization.id !== selectedOrganization?.id) {
                                  dispatch(setSelectedEIOrganization(organization))
                                }
                                onClose()
                              }}
                              className='manage-ad-hoc-dialog__group-link'
                            >
                              {group.name}
                            </Link>
                          </Tooltip>
                        </div>
                        <div className='manage-ad-hoc-dialog__group-action'>
                          <Typography variant='body2' color='textDisabled'>
                            {group.actions.has('write')
                              ? formatActionLabel('write')
                              : group.actions.has('read')
                                ? formatActionLabel('read')
                                : 'No actions'}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {emails.length > 0 ? (
          <>
            <Button variant='outlined' color='inherit' onClick={() => setEmails([])}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleAddUser}
              disabled={adding || !addAction || emails.some((email) => !isValidEmail(email))}
            >
              {adding ? 'Granting...' : 'Grant access'}
            </Button>
          </>
        ) : (
          <>
            <Button variant='outlined' color='inherit' onClick={closeAndClearEmails}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSaveClick}
              disabled={!hasPendingChanges}
              loading={saving}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>

      <Dialog
        open={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Typography variant='h4'>
            Remove {usersQueuedForRemoval.length === 1 ? 'User' : 'Users'}?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='textSecondary'>
            {`The following ${
              usersQueuedForRemoval.length === 1 ? 'user' : 'users'
            } will lose direct ${accessLabel} access when removed:`}
          </Typography>
          <ul className='manage-ad-hoc-dialog__remove-list'>
            {usersQueuedForRemoval.map((user) => (
              <li key={user.id}>
                <Typography variant='body2' component='span'>
                  {user.email || user.name}
                </Typography>
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='inherit' onClick={() => setShowRemoveConfirm(false)}>
            Cancel
          </Button>
          <Button variant='contained' color='error' onClick={handleConfirmRemove}>
            {`Remove ${usersQueuedForRemoval.length === 1 ? 'User' : 'Users'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default ManageAdHocDialog
